import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import { forgotPassword, resetPassword } from "../../services/authService";
import { isValidEmail } from "../../utils/validators";
import { Eye, EyeOff } from "lucide-react";

// Special-character set accepted for the "special character" requirement:
// the original !@#$%^&* plus [ ] ( ) : ; \ < > " - see Login.jsx's copy of
// this same check for why.
const passwordChecks = {
  length: (p) => p.length >= 8,
  uppercase: (p) => /[A-Z]/.test(p),
  lowercase: (p) => /[a-z]/.test(p),
  number: (p) => /\d/.test(p),
  special: (p) => /[!@#$%^&*[\]():;\\<>"]/.test(p),
};

const passwordLabels = {
  length: "At least 8 characters",
  uppercase: "One uppercase letter",
  lowercase: "One lowercase letter",
  number: "One number",
  special: "One special character (e.g. !@#$%^&* [ ] ( ) : ; \\ < > \")",
};

// Two entry points, same as the Form 720 reference this mirrors: with no ?token, this is
// the "request a reset link" form (POST /auth/forgot-password); with ?token=... (the link
// from that email), it's the "set a new password" form (POST /auth/reset-password).
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // Carried over from the login form (see Login.jsx's "Forgot your password?" link) so the
  // user doesn't have to retype an email they already entered once.
  const prefillEmail = searchParams.get("email") || "";

  const [view, setView] = useState(token ? "reset" : "request"); // 'request' | 'sent' | 'reset' | 'done'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState(prefillEmail);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const passwordResults = Object.keys(passwordChecks).map((key) => ({
    key,
    label: passwordLabels[key],
    valid: passwordChecks[key](newPassword),
  }));
  const passwordValid = passwordResults.every((r) => r.valid);
  const confirmValid = confirmPassword === newPassword && confirmPassword.length > 0;

  const handleRequest = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValidEmail(email)) return;
    setError("");
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setView("sent");
    } catch (err) {
      setError(err.message || "Couldn't send the reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!passwordValid || !confirmValid) return;
    setError("");
    setSubmitting(true);
    try {
      await resetPassword({ token, newPassword, confirmPassword });
      setView("done");
    } catch (err) {
      setError(err.message || "This reset link is invalid or has expired. Please request a new one.");
    } finally {
      setSubmitting(false);
    }
  };

  if (view === "sent") {
    return (
      <Panel padded className="w-full max-w-[400px] !p-7 text-center">
        <div className="text-3xl mb-3">✅</div>
        <h1 className="text-2xl mt-1.5">Check your email</h1>
        <p className="text-[var(--muted)] my-1 mb-1">We sent a password reset link to:</p>
        <p className="font-semibold text-[var(--ink)] mb-5">{email}</p>
        <p className="text-xs text-[var(--muted)] mb-6">
          The link expires in 30 minutes. Check your spam folder if you don't see it.
        </p>
        <Link to="/login">
          <Button size="lg" block>
            Back to sign in
          </Button>
        </Link>
      </Panel>
    );
  }

  if (view === "done") {
    return (
      <Panel padded className="w-full max-w-[400px] !p-7 text-center">
        <div className="text-3xl mb-3">✅</div>
        <h1 className="text-2xl mt-1.5">Password updated</h1>
        <p className="text-[var(--muted)] my-1 mb-6">
          Your password has been reset. You can now sign in with your new password.
        </p>
        <Link to="/login">
          <Button size="lg" block>
            Sign in →
          </Button>
        </Link>
      </Panel>
    );
  }

  if (view === "reset") {
    return (
      <Panel padded className="w-full max-w-[400px] !p-7">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Heavy Vehicle Use Tax · TY2026
        </div>
        <h1 className="text-2xl mt-1.5">Set a new password</h1>
        <p className="text-[var(--muted)] my-1 mb-[22px]">Choose a strong password for your account.</p>

        <form onSubmit={handleReset}>
          <FormField label={<>New password <span className="text-red-500">*</span></>}>
            <div className="relative">
              <TextInput
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                invalid={touched && !passwordValid}
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 -mt-2.5 mb-4">
            {passwordResults.map((r) => (
              <div key={r.key} className={["text-xs", r.valid ? "text-[var(--ok)]" : "text-[var(--muted)]"].join(" ")}>
                {r.valid ? "✓" : "•"} {r.label}
              </div>
            ))}
          </div>

          <FormField
            label={<>Confirm password <span className="text-red-500">*</span></>}
            error="Passwords do not match."
            showError={touched && !!confirmPassword && !confirmValid}
          >
            <TextInput
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              invalid={touched && !!confirmPassword && !confirmValid}
              required
            />
          </FormField>

          {error && <div className="text-xs text-[var(--danger)] mb-3">{error}</div>}

          <Button type="submit" size="lg" block disabled={submitting}>
            {submitting ? "Please wait…" : "Reset password"}
          </Button>
        </form>
      </Panel>
    );
  }

  return (
    <Panel padded className="w-full max-w-[400px] !p-7">
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Heavy Vehicle Use Tax · TY2026
      </div>
      <h1 className="text-2xl mt-1.5">Forgot your password?</h1>
      <p className="text-[var(--muted)] my-1 mb-[22px]">Enter your email and we'll send you a reset link.</p>

      {/* noValidate: see Login.jsx's identical comment - the email field
          accepts IRS-legal special characters that the browser's native
          type="email" constraint validation would otherwise block on submit. */}
      <form onSubmit={handleRequest} noValidate>
        <FormField
          label={<>Email <span className="text-red-500">*</span></>}
          error="Enter a valid email address."
          showError={touched && !isValidEmail(email)}
        >
          <TextInput
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={touched && !isValidEmail(email)}
            required
          />
        </FormField>

        {error && <div className="text-xs text-[var(--danger)] mb-3">{error}</div>}

        <Button type="submit" size="lg" block disabled={submitting}>
          {submitting ? "Please wait…" : "Send reset link"}
        </Button>
      </form>

      <p className="text-[var(--muted)] text-xs text-center mt-3.5">
        <Link to="/login" className="text-[var(--accent)] font-semibold underline">← Back to sign in</Link>
      </p>
    </Panel>
  );
};

export default ResetPassword;
